import { cn } from '@repo/tailwind-config/tailwind.functions'
import { TailwindColor, tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { StringFormatter } from '@repo/toolkit/string'
import { FileTypeEnum, IFile, SVGFC } from '@repo/toolkit/types'
import { forwardRef, type InputHTMLAttributes, ReactNode, useState } from 'react'

import useTranslate from '../../../../../../apps/backoffice/src/lib/hooks/use-translate'
import { DeleteIcon, ImageIcon } from '../../../../icons'
import Modal from '../../../molecules/modal'
import ActionButton from '../../action-button'
import Typography, { TextType } from '../../typography'
import { DisplayImage } from './display-image'

export type FileInputOptions = {
  displayDetails?: boolean
}

export interface FileInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  types: FileTypeEnum[]
  placeholder: string
  options?: FileInputOptions
  files?: IFile[]
  hasError?: boolean
  label?: string
  accept?: string
  icon?: SVGFC
  iconContainerClassName?: string
  labelType?: TextType
  placeholderVariant?: TextType
  placeholderColor?: TailwindColor
  description?: ReactNode
  showDeleteIcon?: boolean
  allowedExtensions?: string[]
  previewOnClick?: boolean
  required?: boolean
  allowedAspectRatio?: `${number}:${number}`
  maxSize?: number
  onFilesChange: (files: IFile[]) => void
  onFileError?: (error: string) => void
}

const SHEET_MIME_TYPES = [
  'text/csv',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
]

export function getFileType(file: File): FileTypeEnum {
  const [fileType, extension] = file.type.split('/')

  if (SHEET_MIME_TYPES.includes(file.type)) return FileTypeEnum.SHEET
  // browsers report .xml as application/xml or text/xml
  else if (extension === 'xml') return FileTypeEnum.XML
  else if (file.type === 'application/pdf') return FileTypeEnum.PDF
  else if (fileType === 'video') return FileTypeEnum.VIDEO
  else if (fileType === 'text') return FileTypeEnum.XML
  else return FileTypeEnum.IMAGE
}

// mirrors the input accept attribute, which the drop zone does not enforce
export function isFileAccepted(file: File, accept: string): boolean {
  if (accept === '*') return true
  const extension = `.${file.name.split('.').pop()?.toLowerCase()}`

  return accept.split(',').some((rule) => {
    const trimmedRule = rule.trim().toLowerCase()
    if (trimmedRule.startsWith('.')) return trimmedRule === extension
    if (trimmedRule.endsWith('/*')) return file.type.startsWith(trimmedRule.slice(0, -1))
    return trimmedRule === file.type
  })
}

export function getAcceptAttribute(types: FileTypeEnum[], accept?: string): string {
  if (accept) return accept

  const acceptMap = {
    [FileTypeEnum.IMAGE]: 'image/*',
    [FileTypeEnum.VIDEO]: 'video/*',
    [FileTypeEnum.PDF]: 'application/pdf',
    [FileTypeEnum.XML]: 'application/xml',
    [FileTypeEnum.SHEET]: SHEET_MIME_TYPES.join(',')
  }

  return types.map((type) => acceptMap[type] || '').join(',') || '*'
}

export function DisplayFiles({
  files,
  previewOnClick,
  onDelete
}: {
  files?: IFile[]
  previewOnClick?: boolean
  onDelete?: (files: IFile[]) => void
}) {
  const [selectedFile, setSelectedFile] = useState<IFile>()

  return (
    <>
      <div className={'flex flex-col items-center justify-start gap-2'}>
        {files?.map((document, index) => (
          <div
            key={index}
            className={'flex w-full items-center justify-between p-2 bg-grey-light rounded-lg'}
          >
            <div className={'flex flex-1 items-center gap-1 min-w-0'}>
              <div className={'h-12 w-12 relative shrink-0'}>
                {previewOnClick && (
                  <div
                    className={'cursor-pointer absolute z-40 h-12 w-12'}
                    // keep the preview clickable inside read-only (pointer-events-none) forms
                    style={{ pointerEvents: 'auto' }}
                    onClick={() => {
                      if (document.type === FileTypeEnum.XML) window.open(document.path, '_blank')
                      else setSelectedFile(document)
                    }}
                  />
                )}
                <DisplayImage
                  type={document.type}
                  src={document.path}
                  className={'h-12 w-12 rounded-sm object-cover relative z-5 overflow-hidden'}
                />
              </div>

              <Typography
                variant={'tag'}
                color={tailwindColors.grey.DEFAULT}
                className={'break-all'}
              >
                {document.name}
              </Typography>
            </div>

            {onDelete && (
              <ActionButton
                variant={'danger'}
                icon={DeleteIcon}
                onClick={() => {
                  const newDocuments = files.filter((_, i) => i !== index)
                  onDelete(newDocuments)
                }}
              />
            )}
          </div>
        ))}
      </div>

      {selectedFile && (
        <Modal
          isOpen={!!selectedFile}
          title={selectedFile.name}
          hideActions
          size={'large'}
          onCancel={() => setSelectedFile(undefined)}
        >
          <DisplayImage
            allowAll
            type={selectedFile.type}
            src={selectedFile.path}
            className={'h-[70vh] w-full rounded-sm object-contain relative z-10 overflow-auto'}
          />
        </Modal>
      )}
    </>
  )
}

const UploadFileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      className,
      hasError,
      labelType = 'tag',
      placeholderVariant = 'label',
      placeholderColor = tailwindColors.grey.DEFAULT,
      types,
      multiple,
      label,
      files,
      icon,
      iconContainerClassName,
      placeholder,
      accept,
      showDeleteIcon = true,
      previewOnClick,
      allowedExtensions,
      allowedAspectRatio,
      maxSize,
      required,
      options,
      onFilesChange,
      onFileError,
      ...props
    },
    ref
  ) => {
    const { t } = useTranslate()

    async function validateImage(file: File): Promise<boolean> {
      return new Promise((resolve) => {
        if (!types.includes(FileTypeEnum.IMAGE)) {
          resolve(true)
          return
        }
        const image = new Image()
        image.src = URL.createObjectURL(file)

        image.onload = () => {
          const { width, height } = image

          if (allowedAspectRatio) {
            const [allowedWidth, allowedHeight] = allowedAspectRatio.split(':').map(Number)

            // Calculate the actual aspect ratio of the image
            const actualAspectRatio = width / height
            const expectedAspectRatio = allowedWidth / allowedHeight

            // Check if the image's aspect ratio matches the allowed aspect ratio
            if (actualAspectRatio !== expectedAspectRatio) {
              onFileError?.(`${t('images.aspect.ratio.error')} ${allowedAspectRatio}`)
              resolve(false)
              return
            }
          }

          if (maxSize && file.size > maxSize) {
            onFileError?.(`${t('images.size.error')} ${maxSize / 1024 / 1024} Mo.`)
            resolve(false)
            return
          }

          resolve(true)
        }

        image.onerror = () => {
          onFileError?.(t('images.read.error'))
          resolve(false)
        }
      })
    }

    async function addImage(data: FileList | null) {
      const pendingFiles: IFile[] = []
      const newFiles = data ? Array.from(data) : []

      if (newFiles.length === 0) return

      for (const file of newFiles) {
        // Validate file extension
        if (allowedExtensions && !allowedExtensions.includes(file.name.split('.').pop() || '')) {
          onFileError?.(
            `Veuillez télécharger un fichier avec une extension valide ${allowedExtensions}`
          )
          continue
        }

        if (!isFileAccepted(file, getAcceptAttribute(types, accept))) {
          onFileError?.('Veuillez télécharger un fichier de type valide')
          continue
        }

        const type = getFileType(file)

        // Validate image dimensions
        if (type === FileTypeEnum.IMAGE) {
          const isValid = await validateImage(file)
          if (!isValid) continue // Skip this file if validation fails
        }

        pendingFiles.push({
          name: file.name,
          path: URL.createObjectURL(file),
          size: file.size,
          type
        })
      }

      // Finally update files only if there are valid files
      if (pendingFiles.length > 0) {
        if (!multiple) onFilesChange([pendingFiles[0]])
        else onFilesChange([...(files || []), ...pendingFiles])
      }
    }

    return (
      <div className="flex flex-col gap-2">
        <div className={'flex flex-col gap-2'}>
          {label && (
            <Typography
              variant={labelType}
              color={hasError ? tailwindColors.red.DEFAULT : tailwindColors.grey.DEFAULT}
            >
              {StringFormatter.isRequired(label, required)}
            </Typography>
          )}

          <div
            className={cn(
              'flex w-full px-1 h-40 flex-col relative items-center justify-center rounded-lg bg-transparent border border-dashed border-grey-light-active',
              { 'border-red': hasError },
              className
            )}
          >
            <input
              {...props}
              className={'absolute w-full h-full opacity-0'}
              ref={ref}
              accept={getAcceptAttribute(types, accept) ?? '*'}
              type={'file'}
              disabled={props.disabled || props.readOnly}
              onChange={(e) => addImage(e.target.files)}
              onClick={(e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
                const target = e.target as HTMLInputElement
                target.value = ''
              }}
              multiple={multiple}
              onDragOverCapture={(e) => {
                e.stopPropagation()
                e.preventDefault()
              }}
              onDrop={(e) => {
                e.preventDefault()
                addImage(e.dataTransfer.files)
              }}
            />
            <div
              className={
                '-z-100 flex h-full w-full flex-col items-center justify-center gap-3 rounded-lg p-2'
              }
            >
              <div className={cn('bg-primary-light p-4 rounded-3xl', iconContainerClassName)}>
                {icon ? (
                  icon({
                    color: tailwindColors.primary.DEFAULT,
                    className: 'h-7 w-7'
                  })
                ) : (
                  <ImageIcon className={'fill-primary h-7 w-7'} />
                )}
              </div>

              <Typography
                variant={placeholderVariant}
                className={'text-center'}
                color={placeholderColor}
              >
                {placeholder}
              </Typography>
              {options?.displayDetails && (
                <div className="flex flex-col justify-center items-center gap-1">
                  {allowedExtensions && allowedExtensions.length > 0 && (
                    <Typography variant="small" color={tailwindColors.grey.light.active}>
                      Téléchargez votre fichier uniquement au format&nbsp;
                      <span className="font-medium text-grey-light-active">
                        {allowedExtensions?.map((ext) => ext.toUpperCase())?.join(', ')}
                      </span>
                    </Typography>
                  )}
                  {maxSize && (
                    <Typography variant="small" color={tailwindColors.grey.light.active}>
                      Le fichier ne doit pas dépasser&nbsp;
                      <span className="font-medium text-grey-light-active">
                        {maxSize / 1024 / 1024} Mo
                      </span>
                    </Typography>
                  )}
                  {allowedAspectRatio && (
                    <Typography variant="small" color={tailwindColors.grey.light.active}>
                      Maintenez un rapport d'aspect de&nbsp;
                      <span className="font-medium text-grey-light-active">
                        {allowedAspectRatio}
                      </span>
                    </Typography>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <DisplayFiles
          files={files}
          previewOnClick={previewOnClick}
          onDelete={showDeleteIcon ? onFilesChange : undefined}
        />
      </div>
    )
  }
)

UploadFileInput.displayName = 'Input File'

export { DisplayImage }

export default UploadFileInput
